import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { scrollToId, reservarServicio } from "../lib/booking";

const ease = [0.22, 1, 0.36, 1];

const ELLA_IMG = "/images/nails_spa_hero.jpg";
const EL_IMG =
  "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?auto=format&fit=crop&w=1200&q=80&crop=entropy&cs=srgb&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwYmFyYmVyc2hvcCUyMG1lbiUyMGdyb29taW5nfGVufDB8fHx8MTc4NjM5NDk4N3ww&ixlib=rb-4.1.0";

const Side = ({ side, hovered, setHovered, isMobile, children }) => {
  const isHover = hovered === side;
  const other = hovered && hovered !== side;

  if (isMobile) {
    return (
      <div
        data-testid={`hero-${side}`}
        className="relative w-full min-h-[75vh] flex flex-col justify-end overflow-hidden grain"
      >
        {children}
      </div>
    );
  }

  return (
    <motion.div
      data-testid={`hero-${side}`}
      onMouseEnter={() => setHovered(side)}
      onMouseLeave={() => setHovered(null)}
      animate={{ flexGrow: isHover ? 1.35 : other ? 0.65 : 1 }}
      transition={{ duration: 0.9, ease }}
      className="relative h-screen overflow-hidden grain"
      style={{ flexBasis: 0, flexGrow: 1 }}
    >
      {children}
    </motion.div>
  );
};

const Hero = () => {
  const [hovered, setHovered] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <header className="relative w-full">
      {/* Center brand mark */}
      <div className="pointer-events-none absolute inset-0 z-30 hidden md:flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease, delay: 0.4 }}
          className="flex flex-col items-center"
        >
          <div className="h-16 w-px bg-white/40" />
          <div className="my-6 rounded-2xl border border-white/10 bg-black/30 p-6 backdrop-blur-md shadow-[0_8px_32px_rgba(0,0,0,0.2)]">
            <div className="w-36 md:w-48 lg:w-56">
              <img
                src="/images/duo_experience_w.svg"
                alt="DUO Experience - Spa de lujo para ella y para él"
                className="h-auto w-full object-contain"
                decoding="async"
              />
            </div>
          </div>
          <div className="h-16 w-px bg-white/40" />
        </motion.div>
      </div>

      <div className="flex flex-col md:flex-row w-full">
        {/* PARA ELLA */}
        <Side side="ella" hovered={hovered} setHovered={setHovered} isMobile={isMobile}>
          <motion.img
            src={ELLA_IMG}
            alt="Experiencia Para Ella"
            decoding="async"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover animate-fade-in"
            animate={{ scale: !isMobile && hovered === "ella" ? 1.08 : 1 }}
            transition={{ duration: 1.1, ease }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#faf9f6]/95 via-[#faf9f6]/40 to-[#f0e4e1]/30" />
          <div className="relative z-20 flex flex-col justify-end min-h-[75vh] md:h-full p-6 md:p-14 lg:p-20">
            <motion.span
              className="label text-[#cea498] font-bold tracking-widest text-[10px] md:text-xs"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease }}
            >
              NAILS SPA
            </motion.span>
            <div className="reveal-mask mt-3 md:mt-4">
              <motion.h1
                className="font-serif-display text-[#593d36] text-3xl sm:text-5xl lg:text-6xl leading-none"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.7, ease }}
              >
                El detalle
              </motion.h1>
            </div>
            <div className="reveal-mask">
              <motion.h1
                className="font-serif-display text-[#593d36] text-3xl sm:text-5xl lg:text-6xl leading-none"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.76, ease }}
              >
                que transforma
              </motion.h1>
            </div>
            <div className="reveal-mask">
              <motion.h1
                className="font-serif-display italic text-[#593d36] text-3xl sm:text-5xl lg:text-6xl leading-tight"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.82, ease }}
              >
                tu ritual
              </motion.h1>
            </div>
            <motion.p
              className="mt-3 md:mt-5 max-w-sm text-xs md:text-base text-[#5a4f4b] leading-relaxed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1, ease }}
            >
              Manicure y pedicure de precisión, cuidado y acabados impecables para que cada gesto hable de ti.
            </motion.p>
            <motion.button
              data-testid="hero-ella-cta"
              onClick={() => reservarServicio("ella", "Nails Spa")}
              className="group mt-5 md:mt-7 inline-flex w-fit items-center gap-3 rounded-full bg-[#cea498] px-6 py-3 md:px-7 md:py-3.5 text-[#593d36] font-bold border border-[#593d36]/15"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.15, ease }}
              whileHover={!isMobile ? { backgroundColor: "#593d36", color: "#faf9f6" } : {}}
            >
              <span className="label font-bold text-[10px] md:text-xs">RESERVAR NAILS SPA</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </motion.button>
          </div>
        </Side>

        {/* PARA ÉL */}
        <Side side="el" hovered={hovered} setHovered={setHovered} isMobile={isMobile}>
          <motion.img
            src={EL_IMG}
            alt="Experiencia Para Él"
            decoding="async"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover animate-fade-in"
            animate={{ scale: !isMobile && hovered === "el" ? 1.08 : 1 }}
            transition={{ duration: 1.1, ease }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111]/95 via-[#1a1a1a]/60 to-[#1a1a1a]/40" />
          <div className="relative z-20 flex flex-col justify-end min-h-[75vh] md:h-full p-6 md:p-14 lg:p-20 md:items-end md:text-right">
            <motion.span
              className="label text-[#b79b7d] text-[10px] md:text-xs tracking-widest"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6, ease }}
            >
              Para Él
            </motion.span>
            <div className="reveal-mask mt-3 md:mt-4">
              <motion.h1
                className="font-serif-display text-[#faf9f6] text-3xl sm:text-5xl lg:text-6xl leading-none"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.7, ease }}
              >
                El territorio
              </motion.h1>
            </div>
            <div className="reveal-mask">
              <motion.h1
                className="font-serif-display italic text-[#b79b7d] text-3xl sm:text-5xl lg:text-6xl leading-tight"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1, delay: 0.82, ease }}
              >
                del carácter
              </motion.h1>
            </div>
            <motion.p
              className="mt-3 md:mt-5 max-w-sm text-xs md:text-base text-[#cfc7c0] leading-relaxed md:ml-auto"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1, ease }}
            >
              Un espacio oscuro y sobrio, de charcoal y bronce, donde el cuidado se vive con precisión y presencia.
            </motion.p>
            <motion.button
              data-testid="hero-el-cta"
              onClick={() => scrollToId("experiencia-el")}
              className="group mt-5 md:mt-7 inline-flex w-fit items-center gap-3 rounded-full border border-[#b79b7d] bg-transparent px-6 py-3 md:px-7 md:py-3.5 text-[#faf9f6]"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.15, ease }}
              whileHover={!isMobile ? { backgroundColor: "#b79b7d", color: "#1a1a1a" } : {}}
            >
              {!isMobile && <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />}
              <span className="label text-[10px] md:text-xs">Ver experiencia de él</span>
              {isMobile && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
            </motion.button>
          </div>
        </Side>
      </div>
    </header>
  );
};

export default Hero;
